import type React from 'react';

import { Box, FormField, FormHelp, Heading, Input, Label } from '@zillow/constellation-10';

import type { PeopleSection } from '../../../models/index';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';
import { EMERGENCY_CONTACT } from '../../../constants';
import PhoneInput from '../../Widgets/PhoneInput';

type Props = {
  section: PeopleSection;
  setSection: (section: PeopleSection) => void;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function EmergencyContact({ section, setSectionValues }: Props): JSX.Element {
  function setFirstName(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [EMERGENCY_CONTACT.FIELD_NAME.FIRST_NAME]: event.currentTarget.value,
    });
  }

  function setLastName(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [EMERGENCY_CONTACT.FIELD_NAME.LAST_NAME]: event.currentTarget.value,
    });
  }

  function onPhoneNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [EMERGENCY_CONTACT.FIELD_NAME.PHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs" css={{ marginBottom: 'layout.tight' }}>
        Who should the tenant contact in case there is an emergency with the property?
      </Heading>
      <Box
        className={FULLSTORY_EXCLUDE}
        css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'layout.tighter' }}
      >
        <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
          <FormField
            required
            css={{ marginBottom: 'layout.tighter' }}
            label={<Label id="first-name">First name</Label>}
            control={
              <Input value={section.getFieldValue(EMERGENCY_CONTACT.FIELD_NAME.FIRST_NAME)} onChange={setFirstName} />
            }
          />
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
          <FormField
            required
            css={{ marginBottom: 'layout.tighter' }}
            label={<Label id="last-name">Last name</Label>}
            control={
              <Input value={section.getFieldValue(EMERGENCY_CONTACT.FIELD_NAME.LAST_NAME)} onChange={setLastName} />
            }
          />
        </Box>
        <Box css={{ gridColumnEnd: 'span 12' }}>
          <FormField
            required
            error={section.getField(EMERGENCY_CONTACT.FIELD_NAME.PHONE_NUMBER)?.hasError}
            label={<Label>Phone number</Label>}
            control={
              <PhoneInput
                value={section.getFieldValue(EMERGENCY_CONTACT.FIELD_NAME.PHONE_NUMBER)}
                error={section.getField(EMERGENCY_CONTACT.FIELD_NAME.PHONE_NUMBER)?.hasError}
                onChange={onPhoneNumberChange}
              />
            }
            description={
              section.getField(EMERGENCY_CONTACT.FIELD_NAME.PHONE_NUMBER)?.hasError && (
                <FormHelp>{section.getField(EMERGENCY_CONTACT.FIELD_NAME.PHONE_NUMBER)?.getErrorMessage()}</FormHelp>
              )
            }
          />
        </Box>
      </Box>
    </>
  );
}
